import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CBadge,
  CProgress,
  CWidgetStatsA,
  CSpinner,
  CCallout,
  CButton,
  CButtonGroup,
} from "@coreui/react";
import { CChartBar, CChartLine, CChartPie } from "@coreui/react-chartjs";
import { cilPeople, cibAppStore, cilCalendar, cilChart } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import formatter from "src/reusables/formatter";
import { getDashboardMetrics, getMostVisitedStores, getVisitTrends } from "src/services/Analytics";

function Dashboard() {
  const [data, setData] = useState({
    metrics: {
      visits: 0,
      plannedVisits: 0,
      outstandingPayments: 0,
      overdueVisits: 0,
    },
    stockData: [],
    visitTrends: [],
    topStores: [],
  });

  const [loading, setLoading] = useState({
    metrics: true,
    stock: true,
    trends: true,
    stores: true,
  });

  const [error, setError] = useState({
    metrics: null,
    stock: null,
    trends: null,
    stores: null,
  });

  const [trendsPeriod, setTrendsPeriod] = useState("7days");

  // Check if any data is still loading
  const isLoading = Object.values(loading).some((val) => val);
  // Check if any error occurred
  const hasError = Object.values(error).some((val) => val !== null);

  // Fetch all dashboard data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch metrics
        setLoading((prev) => ({ ...prev, metrics: true }));
        setError((prev) => ({ ...prev, metrics: null }));
        const metricsResponse = await fetchDashboardMetrics();
        setData((prev) => ({ ...prev, metrics: metricsResponse }));
      } catch (err) {
        console.error("Error fetching metrics:", err);
        setError((prev) => ({ ...prev, metrics: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, metrics: false }));
      }

      try {
        // Fetch stock data
        setLoading((prev) => ({ ...prev, stock: true }));
        setError((prev) => ({ ...prev, stock: null }));
        const stockResponse = await getStockSummary();
        setData((prev) => ({ ...prev, stockData: stockResponse }));
      } catch (err) {
        console.error("Error fetching stock data:", err);
        setError((prev) => ({ ...prev, stock: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, stock: false }));
      }

      try {
        // Fetch top stores
        setLoading((prev) => ({ ...prev, stores: true }));
        setError((prev) => ({ ...prev, stores: null }));
        const topStoresResponse = await fetchTopStores();
        console.log("Top stores data:", topStoresResponse);
        setData((prev) => ({ ...prev, topStores: topStoresResponse }));
      } catch (err) {
        console.error("Error fetching top stores:", err);
        setError((prev) => ({ ...prev, stores: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, stores: false }));
      }
    };

    fetchData();
  }, []);

  // Separate effect for visit trends to handle period changes
  useEffect(() => {
    console.log("Fetching visit trends for period:", trendsPeriod);
    const fetchVisitTrendsData = async () => {
      try {
        setLoading((prev) => ({ ...prev, trends: true }));
        setError((prev) => ({ ...prev, trends: null }));
        const trendsResponse = await fetchVisitTrends(trendsPeriod);
        console.log("Visit trends data:", trendsResponse);
        setData((prev) => ({ ...prev, visitTrends: trendsResponse }));
      } catch (err) {
        console.error("Error fetching visit trends:", err);
        setError((prev) => ({ ...prev, trends: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, trends: false }));
      }
    };

    fetchVisitTrendsData();
  }, [trendsPeriod]);

  const handleTrendsPeriodChange = (period) => {
    setTrendsPeriod(period);
  };

  const getTrendsTitle = () => {
    switch (trendsPeriod) {
      case "today":
        return "Today's Visit Trends (Hourly)";
      case "7days":
        return "Visit Trends (Last 7 Days)";
      case "biweekly":
        return "Visit Trends (Last 14 Days)";
      case "month":
        return "Visit Trends (Last 30 Days)";
      default:
        return "Visit Trends";
    }
  };

  if (isLoading && !hasError) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <div className="text-center">
          <CSpinner color="primary" size="lg" />
          <p className="mt-3">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ color: "black" }}>
      {/* Top Metrics Cards */}
      <CRow className="mb-4">
        <CCol md={3}>
          <CWidgetStatsA
            color="primary"
            value={data.metrics.visits}
            title="Today's Visits"
            icon={<CIcon icon={cilPeople} height={36} />}
          />
        </CCol>
        <CCol md={3}>
          <CWidgetStatsA
            color="info"
            value={data.metrics.plannedVisits}
            title="Planned Visits"
            icon={<CIcon icon={cilCalendar} height={36} />}
          />
        </CCol>
        <CCol md={3}>
          <CWidgetStatsA
            color="warning"
            value={formatter("currency", data.metrics.outstandingPayments)}
            title="Outstanding Payments"
            icon={<CIcon icon={cilChart} height={36} />}
          />
        </CCol>
        <CCol md={3}>
          <CWidgetStatsA
            color="danger"
            value={data.metrics.overdueVisits}
            title="Overdue Visits"
            icon={<CIcon icon={cibAppStore} height={36} />}
          />
        </CCol>
      </CRow>

      {/* Stock Availability Summary */}
      {!loading.stock && (
        <CCard className="mb-4">
          <CCardHeader>
            <h5>Stock Availability Summary</h5>
          </CCardHeader>
          <CCardBody>
            <CRow className="g-3">
              {data.stockData.map((category) => (
                <CCol md={6} lg={3} key={category.categoryID}>
                  <CCard
                    className="h-100"
                    style={{
                      borderLeft: `4px solid ${
                        category.availabilityPercentage >= 50
                          ? "#2eb85c"
                          : category.availabilityPercentage >= 25
                          ? "#f9b115"
                          : "#e55353"
                      }`,
                    }}
                  >
                    <CCardBody>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6>{category.categoryName}</h6>
                        <CBadge
                          color={
                            category.availabilityPercentage >= 50
                              ? "success"
                              : category.availabilityPercentage >= 25
                              ? "warning"
                              : "danger"
                          }
                        >
                          {category.availabilityPercentage}%
                        </CBadge>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">
                          {category.availableProducts} of {category.totalProducts} products available
                        </small>
                      </div>
                      <CProgress
                        color={
                          category.availabilityPercentage >= 50
                            ? "success"
                            : category.availabilityPercentage >= 25
                            ? "warning"
                            : "danger"
                        }
                        value={category.availabilityPercentage}
                        className="mb-3"
                      />
                      <div className="d-flex justify-content-between">
                        <small>Low: {category.lowStockProducts}</small>
                        <small>Out: {category.outOfStockProducts}</small>
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol>
              ))}
            </CRow>
          </CCardBody>
        </CCard>
      )}

      {/* Visit Trends */}
      <CRow className="mb-4">
        <CCol md={8}>
          <CCard>
            <CCardHeader className="d-flex justify-content-between align-items-center">
              <h5>{getTrendsTitle()}</h5>
              <CButtonGroup>
                <CButton
                  color="outline-secondary"
                  size="sm"
                  active={trendsPeriod === "today"}
                  onClick={() => handleTrendsPeriodChange("today")}
                >
                  Today
                </CButton>
                <CButton
                  color="outline-secondary"
                  size="sm"
                  active={trendsPeriod === "7days"}
                  onClick={() => handleTrendsPeriodChange("7days")}
                >
                  7 Days
                </CButton>
                <CButton
                  color="outline-secondary"
                  size="sm"
                  active={trendsPeriod === "biweekly"}
                  onClick={() => handleTrendsPeriodChange("biweekly")}
                >
                  14 Days
                </CButton>
                <CButton
                  color="outline-secondary"
                  size="sm"
                  active={trendsPeriod === "month"}
                  onClick={() => handleTrendsPeriodChange("month")}
                >
                  30 Days
                </CButton>
              </CButtonGroup>
            </CCardHeader>
            <CCardBody>
              {loading.trends ? (
                <div className="text-center py-4">
                  <CSpinner />
                </div>
              ) : (
                <CChartLine
                  data={{
                    labels: data.visitTrends.map((t) => t.date),
                    datasets: [
                      {
                        label: "Completed Visits",
                        backgroundColor: "rgba(78, 91, 151, 0.2)",
                        borderColor: "#4E5B97",
                        pointBackgroundColor: "#4E5B97",
                        pointBorderColor: "#fff",
                        data: data.visitTrends.map((t) => t.completed),
                      },
                      {
                        label: "Planned Visits",
                        backgroundColor: "rgba(48, 111, 186, 0.2)",
                        borderColor: "#de148c",
                        pointBackgroundColor: "#306fba",
                        pointBorderColor: "#fff",
                        data: data.visitTrends.map((t) => t.planned),
                      },
                    ],
                  }}
                  options={{
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          precision: 0,
                        },
                      },
                    },
                  }}
                />
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={4}>
          <CCard className="h-100">
            <CCardHeader>
              <h5>Top 10 Visited Stores</h5>
            </CCardHeader>
            <CCardBody>
              {loading.stores ? (
                <div className="text-center py-4">
                  <CSpinner />
                </div>
              ) : data.topStores.length > 0 ? (
                data.topStores.map((store, index) => (
                  <a
                    href={"/stores/details/" + store.store_id}
                    key={store.store_id}
                    className="text-decoration-none text-dark"
                  >
                    <div key={store.store_id} className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>
                          <CBadge color="primary" className="me-2">
                            {index + 1}
                          </CBadge>
                          {store.storeName}
                        </span>
                        <CBadge color="secondary">{store.visitCount} visits</CBadge>
                      </div>
                    </div>
                  </a>
                ))
              ) : (
                <div className="text-center text-muted py-4">No store data available</div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Stock Details by Category */}
      {!loading.stock && data.stockData.length > 0 && (
        <CCard className="mb-4">
          <CCardHeader>
            <h5>Stock Details by Category</h5>
          </CCardHeader>
          <CCardBody>
            {data.stockData.map((category) => (
              <div key={category.categoryID} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>{category.categoryName}</h6>
                  <CBadge color="info">{category.products.length} products</CBadge>
                </div>
                <CRow className="g-2">
                  {category.products.slice(0, 4).map((product) => (
                    <CCol md={3} key={product.productID}>
                      <div className="p-3 border rounded">
                        <div className="d-flex justify-content-between">
                          <strong>{product.productName}</strong>
                          <CBadge color={product.stock > 0 ? "success" : "danger"}>{product.stock}</CBadge>
                        </div>
                        <small className={product.stock > 0 ? "text-success" : "text-danger"}>
                          {product.stock > 0 ? "In Stock" : "Out of Stock"}
                        </small>
                      </div>
                    </CCol>
                  ))}
                  {category.products.length > 4 && (
                    <CCol md={3}>
                      <div className="p-3 border rounded text-center">
                        <strong>+{category.products.length - 4} more</strong>
                      </div>
                    </CCol>
                  )}
                </CRow>
              </div>
            ))}
          </CCardBody>
        </CCard>
      )}
    </div>
  );
}

// API functions
async function fetchDashboardMetrics() {
  try {
    const response = await getDashboardMetrics();
    if (response.success) {
      return (
        response.metrics || {
          visits: 0,
          plannedVisits: 0,
          outstandingPayments: 0,
          overdueVisits: 0,
        }
      );
    }
    throw new Error(response.message || "Failed to fetch metrics");
  } catch (error) {
    console.error("Error in fetchDashboardMetrics:", error);
    throw error;
  }
}

async function getStockSummary() {
  return [
    {
      categoryID: 1,
      categoryName: "Product",
      totalProducts: 24,
      availableProducts: 7,
      availabilityPercentage: 29,
      lowStockProducts: 3,
      outOfStockProducts: 14,
      products: [
        { productID: 43, productName: "Icelands Strawberry", stock: 2, isAvailable: 1 },
        // ... other products
      ],
    },
    // ... other categories
  ];
}

async function fetchVisitTrends(period = "7days") {
  try {
    const response = await getVisitTrends(period);
    if (response.success) {
      return response.data || [];
    }
    throw new Error(response.message || "Failed to fetch visit trends");
  } catch (error) {
    console.error("Error in fetchVisitTrends:", error);
    throw error;
  }
}

async function fetchTopStores() {
  try {
    const response = await getMostVisitedStores();
    if (response.success) {
      return response.stores || [];
    }
    throw new Error(response.message || "Failed to fetch most visited stores");
  } catch (error) {
    console.error("Error in fetchVisitTrends:", error);
    throw error;
  }
}

export default Dashboard;
